import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, SettingOutlined, EditOutlined, DeleteOutlined, ImportOutlined, ExportOutlined, FileOutlined } from "@ant-design/icons";
import { 
  useGetUsimSkusQuery, 
  usePostUsimSkuMutation,
  usePutUsimSkuMutation,
  useDeleteUsimSkuMutation, 
  useGetUsimSkuUploadJobsQuery
} from "../../../../api/usim/uploads/products/productSkuApi";
import { useGetProductTypesQuery } from "../../../../api/usim/uploads/products/productTypeApi";
import { UsimSkuItem } from "../../../../api/usim/uploads/products/usimSkuItem";
import { BASE_URL } from "../../../../api/emptyV2Api";
import handleUploadFile from "../../../../utils/uploadFileS3";
import UploadProgressModal from "./UploadProgressModal";
import ProductManageModal from "./productManageModal";
import UploadJobTable from "./UploadJobTable";
import PlanDetailModal from "./PlanDetailModal";

const { Title } = Typography;
const { TextArea } = Input;
const ENDPOINT = "usim/product-plans/upload?key=input"
// Updated interface to match API response
interface UsimSku {
  id: string;
  type: string;
  description: string;
  planName: string;
  status: string | null;
}
type UploadFileGenUrlResult = {
  objectId: string;
  uploadUrl: string;
  fileUrl: string;
}


export default function UsimSkuPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // API hooks
  const { data: apiData, error, isFetching, refetch } = useGetUsimSkusQuery();
  const { data: productTypesData } = useGetProductTypesQuery();
  const [postUsimSku] = usePostUsimSkuMutation();
  const [putUsimSku] = usePutUsimSkuMutation();
  const [deleteUsimSku] = useDeleteUsimSkuMutation();
  const { data: uploadJobsData , error: uploadJobsError, isFetching: uploadJobsFetching} = useGetUsimSkuUploadJobsQuery();
  
  const [filteredData, setFilteredData] = useState<UsimSkuItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [editingData, setEditingData] = useState<UsimSkuItem | undefined>();
  const [searchText, setSearchText] = useState("");
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [showUploadJobs, setShowUploadJobs] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<string>("");
  // Plan detail modal states
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedUsimSku, setSelectedUsimSku] = useState<UsimSkuItem | null>(null);

  // Upload progress states
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string>("");

  // Transform API data to include keys for table
  const transformedData: UsimSkuItem[] = React.useMemo(() => {
    if (!apiData) return [];
    return apiData.map((item: UsimSkuItem, index: number) => ({
      key: `${item.name}-${index}`,
      id: item.id ?? "",
      typeName: item.typeName,
      description: item.description,
      name: item.name,
      status: item.status ?? "",
      typeId: item.typeId ?? "",
    }));
  }, [apiData]);

  // Transform product types for dropdown options
  const productTypeOptions = React.useMemo(() => {
    if (!productTypesData) return [];
    // do not show item if status is INACTIVE
    return productTypesData.filter(item => item.status !== false).map(item => ({
      label: item.name,
      value: item.name,
      id: item.id!
    }));
  }, [productTypesData]);

  const showOptions = (show: boolean) => {
    setIsOptionsVisible(show);
  };

  // Column mapping for export (customize export headers here)
  const exportColumnMapping = {
    id: "id",
    productType: "Product Type",
    UsimSku: "Product Plan",
    description: "Description", 
    status: "Status"
  };

  // Handle data export
  const handleExportData = () => {
    try {
      if (filteredData.length === 0) {
        message.warning("No data to export");
        return;
      }

      // Prepare data for export
      const exportData = filteredData.map(item => ({
        [exportColumnMapping.id]: item.id,
        [exportColumnMapping.productType]: item.typeName,
        [exportColumnMapping.description]: item.description || "",
        [exportColumnMapping.UsimSku]: item.name,
        [exportColumnMapping.status]: item.status,
      }));

      // Convert to CSV format
      const headers = Object.values(exportColumnMapping);
      const csvContent = [
        headers.join(","), // Header row
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || "";
            // Escape quotes and wrap in quotes if contains comma or quote
            return value.includes(",") || value.includes('"') 
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
          }).join(",")
        )
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `product-plans-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`Exported ${exportData.length} product plans successfully`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
    }
  };

  // Reset upload states
  const resetUploadStates = () => {
    setUploadProgress(0);
    setIsUploadComplete(false);
    setIsUploadError(false);
    setUploadFileName("");
  };

  // Handle upload modal close
  const handleUploadModalClose = () => {
    setIsUploadModalVisible(false);
    resetUploadStates();
    if (isUploadComplete) {
      refetch(); // Refresh data if upload was successful
    }
  };

async function handleUploadFileWithResult(url: string, f: File, isReplace?: boolean)
  : Promise<UploadFileGenUrlResult | null> {

  console.log("file", f);

  const body: any = { 
    filename: f.name,
    contentType: f.type || "application/octet-stream"
  };
  if (isReplace !== undefined) {
    body.isReplace = isReplace;
  }

  // Generate presigned URL
  const resGenUrl = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!resGenUrl.ok) {
    console.error("Failed to generate presigned URL");
    return null;
  }

  const result = await resGenUrl.json();

  // Upload to S3
  const resUpload = await fetch(result.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": f.type || "application/octet-stream",
    },
    body: f,
  });

  const uploadText = await resUpload.text(); // ✔ read only once
  console.log("text of resupload", uploadText);

  if (!resUpload.ok) {
    console.error("S3 upload failed", resUpload.status, uploadText);
    return null;
  }

  return result;
}

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // console.log("File selected:", file.name);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Initialize upload states
    resetUploadStates();
    setUploadFileName(file.name);
    setIsUploadModalVisible(true);

    try {
      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90; // Keep at 90% until actual upload completes
          }
          return prev + 10;
        });
      }, 200);

      // Perform the actual upload
      await handleUploadFileWithResult(BASE_URL + ENDPOINT, file, false);
      
      // Clear interval and set to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsUploadComplete(true);
      
      message.success("Product plans file uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      setIsUploadError(true);
      setUploadProgress(0);
      message.error("Failed to upload file. Please try again.");
    }
  };

  // Handle import
  const handleImport = () => {
    // Trigger file input click to open system file explorer
    fileInputRef.current?.click();
  };

  // Handle export
  const handleExport = () => {
    handleExportData();
  };

  // Handle view import status
  const handleViewImportStatus = () => {
    // message.info("View import status functionality coming soon");
    setShowUploadJobs(true);
  };

  // Dropdown menu items
  const optionsMenuItems = [
    {
      key: "import",
      label: "Import",
      icon: <ImportOutlined />,
      onClick: handleImport,
    },
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: handleExport,
    },
    {
      key: "View Import Status",
      label: "View Import Status",
      icon: <FileOutlined />,
      onClick: handleViewImportStatus,
    },
   
  ];

  // Update filtered data when API data changes
  useEffect(() => {
    // console.log("transformedData", transformedData);
    setFilteredData(transformedData);
  }, [transformedData]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredData(transformedData);
      return;
    }
    
    const filtered = transformedData.filter(item =>
      item.typeName.toLowerCase().includes(value.toLowerCase()) ||
      item.name.toLowerCase().includes(value.toLowerCase()) ||
      (item.description?.toLowerCase() || "").includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  // Handle add new product plan
  const handleAdd = () => {
    setIsUpdateMode(false);
    setEditingData(undefined);
    setIsModalVisible(true);
  };

  // Handle modal submit
  const handleModalSubmit = async (data: UsimSkuItem) => {
    try {
      if (isUpdateMode) {
        // Update existing product plan
        // console.log("data", data);
        await putUsimSku(data).unwrap();
        message.success("Product SKU updated successfully");
      } else {
        // Add new product SKU
        // console.log("data of sku", data)
        await postUsimSku(data).unwrap();
        message.success("Product SKU added successfully");
      }
      
      refetch(); // Refresh the data
    } catch (error) {
      console.error('Failed to save product plan:', error);
      message.error(`Failed to ${isUpdateMode ? 'update' : 'add'} product plan. Please try again.`);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalVisible(false);
    setIsUpdateMode(false);
    setEditingData(undefined);
  };

  // Handle detail modal close
  const handleDetailModalClose = () => {
    setIsDetailModalVisible(false);
    setSelectedUsimSku(null);
  };

  // Handle status change in detail modal
  const handleStatusChange = async (UsimSku: UsimSkuItem) => {
    try {
      const updatedUsimSku = {
        ...UsimSku,
        status: UsimSku.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      };
      
      await putUsimSku(updatedUsimSku).unwrap();
      message.success(`Product plan status updated to ${updatedUsimSku.status}`);
      refetch(); // Refresh the data
      
      // Update the selected product plan in the modal
      setSelectedUsimSku(updatedUsimSku);
    } catch (error) {
      console.error('Failed to update product plan status:', error);
      message.error("Failed to update product plan status. Please try again.");
    }
  };

  // Handle row selection
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Handle delete product plan
  const handleDelete = async (key: string) => {
    const productToDelete = transformedData.find(item => item.id === key);
    if (!productToDelete) return;
    console.log("productToDelete", productToDelete);
    Modal.confirm({
      title: "Delete Product Plan",
      content: "Are you sure you want to delete this product plan?",
      onOk: async () => {
        try {
          const deleteItem: UsimSkuItem = {
            id: productToDelete.id,
            typeName: productToDelete.typeName,
            description: productToDelete.description,
            name: productToDelete.name,
            typeId: productToDelete.typeId,
          };
            console.log('delete item', deleteItem)
          await deleteUsimSku(deleteItem).unwrap();
          setSelectedRowKeys(prev => prev.filter(k => k !== key));
          message.success("Product plan deleted successfully");
          refetch(); // Refresh the data
        } catch (error) {
          console.error('Failed to delete product plan:', error);
          message.error("Failed to delete product plan. Please try again.");
        }
      },
    });
  };

  // Handle row click to show detail modal
  const handleRowClick = (record: UsimSkuItem) => {
    const UsimSkuItem: UsimSkuItem = {
      id: record.id,
      typeName: record.typeName,
      description: record.description,
      name: record.name,
      status: record.status,
      typeId: record.typeId,
    };
    setSelectedUsimSku(UsimSkuItem);
    setIsDetailModalVisible(true);
  };

  // Handle edit product plan
  const handleEdit = (key: string) => {
    const productToEdit = transformedData.find(item => item.id === key);
    if (productToEdit) {
      setIsUpdateMode(true);
      setEditingData({
        id: productToEdit.id,
        typeName: productToEdit.typeName,
        description: productToEdit.description,
        name: productToEdit.name,
        typeId: productToEdit.typeId,
      });
      setIsModalVisible(true);
    }
  };

  // Handle bulk edit
  const handleBulkEdit = () => {
    message.info("Bulk edit functionality coming soon");
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    Modal.confirm({
      title: "Delete Selected Items",
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected product plan(s)?`,
      onOk: async () => {
        try {
          const deletePromises = selectedRowKeys.map(async (key) => {
            const productToDelete = transformedData.find(item => item.id === key);
            if (productToDelete) {
              const deleteItem: UsimSkuItem = {
                id: productToDelete.id,
                typeName: productToDelete.typeName,
                description: productToDelete.description,
                name: productToDelete.name,
                typeId: productToDelete.typeId,
              };
              console.log("dlete item", deleteItem)
              return deleteUsimSku(deleteItem).unwrap();
            }
          });
          
          await Promise.all(deletePromises);
          setSelectedRowKeys([]);
          message.success(`${selectedRowKeys.length} product plan(s) deleted successfully`);
          refetch(); // Refresh the data
        } catch (error) {
          console.error('Failed to delete product plans:', error);
          message.error("Failed to delete some product plans. Please try again.");
        }
      },
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns: ColumnsType<UsimSkuItem> = [
    {
      title: "Product Type",
      dataIndex: "typeName",
      key: "typeName",
      sorter: (a, b) => a.typeName.localeCompare(b.typeName),
    },
    
    {
      
      title: "Product Plan",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      
      title: "Product Plan id",
      dataIndex: "id",
      key: "id",
    },
    
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) => description || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => status ? "Active" : "Inactive",
      // sorter: (a, b) => a.status?.localeCompare(b.status || ""),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record.id);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  // Handle API errors
  if (error) {
    console.error('API Error:', error);
  }

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Title level={2}>Product Plans</Title>
      
      {/* Hidden file input for import functionality */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv,.xlsx,.xls"
        onChange={handleFileUpload}
      />
      
      {/* Header with search and buttons */}
      <Row justify="space-between" align="middle">
        <Col>
          {selectedRowKeys.length > 0 && (
            <Space>
              <Button 
                icon={<EditOutlined />}
                onClick={handleBulkEdit}
              >
                Edit ({selectedRowKeys.length})
              </Button>
              <Button 
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
              >
                Delete ({selectedRowKeys.length})
              </Button>
              <Button icon={<SettingOutlined />}>
                Options
              </Button>
            </Space>
          )}
          {showUploadJobs && (
            <Button icon={<FileOutlined />} onClick={() => setShowUploadJobs(false)}>
              View Plans
            </Button>
          )}
        </Col>
        <Col>
          <Space>
            <Input.Search
              placeholder="Search product plans"
              allowClear
              style={{ width: 250 }}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add
            </Button>
            <Dropdown
              menu={{ items: optionsMenuItems }}
              trigger={['hover']}
              open={isOptionsVisible}
              onOpenChange={showOptions}
            >
              <Button icon={<SettingOutlined />}>
                Options
              </Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      {/* Table */}
      {showUploadJobs && (
        <UploadJobTable/>
      )}
     {!showUploadJobs && (
      
      <Table
        rowSelection={rowSelection}
        columns={columns}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        dataSource={filteredData}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: [5, 10, 20, 50],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} records`,
        }}
        bordered
        size="small"
        loading={isFetching}
      />
     )}
      {/* Product Plan Manage Modal */}
      <ProductManageModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        isUpdate={isUpdateMode}
        data={editingData}
        productTypeOptions={productTypeOptions}
      />

      {/* Upload Progress Modal */}
      <UploadProgressModal
        isVisible={isUploadModalVisible}
        progress={uploadProgress}
        isComplete={isUploadComplete}
        isError={isUploadError}
        fileName={uploadFileName}
        onClose={handleUploadModalClose}
      />

      {/* Plan Detail Modal */}
      <PlanDetailModal
        isVisible={isDetailModalVisible}
        onClose={handleDetailModalClose}
        usimSku={selectedUsimSku}
        onStatusChange={handleStatusChange}
      />
    </Flex>
  );
}
